import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Field, SelectField, inputCls } from "./Field";
import { api, errMsg } from "../../lib/api";
import { useHumanCheck } from "./HumanCheck";

const EMPTY = { name: "", mobile: "", email: "", employer: "", category: "", subject: "", description: "", confidential: true, urgency: "normal" };

export const CaseForm = ({ kind, categories, submitLabel }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const p = `${kind}-form`;
  const human = useHumanCheck(p);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Please choose a category.");
    setBusy(true);
    try {
      const payload = { ...form, email: form.email || null, ...human.payload };
      if (kind !== "legal") delete payload.urgency;
      const { data } = await api.post(kind === "legal" ? "/legal-requests" : "/grievances", payload);
      setDone(data.ref_id);
      setForm(EMPTY);
      toast.success("Submitted successfully");
      human.refresh();
    } catch (err) {
      toast.error(errMsg(err));
      human.handleError(err);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-leaf/30 bg-leaf-light/50 p-8 text-center" data-testid={`${p}-success`}>
        <CheckCircle2 className="mx-auto h-12 w-12 text-leaf" />
        <h3 className="mt-4 font-display text-xl font-bold text-navy">Your request has been received</h3>
        <p className="mt-2 text-sm text-slate-600">Please keep this reference number for future communication.</p>
        <p className="mt-4 font-mono text-2xl font-bold text-royal" data-testid={`${p}-ref`}>{done}</p>
        <button onClick={() => setDone(null)} className="btn-outline mt-6" data-testid={`${p}-new`}>Submit another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid={p}>
      <Field label="Full Name" required><Input className={inputCls} required value={form.name} onChange={set("name")} data-testid={`${p}-name`} /></Field>
      <Field label="Mobile Number" required><Input className={inputCls} required inputMode="numeric" maxLength={13} value={form.mobile} onChange={set("mobile")} data-testid={`${p}-mobile`} /></Field>
      <Field label="Email Address"><Input className={inputCls} type="email" value={form.email} onChange={set("email")} data-testid={`${p}-email`} /></Field>
      <Field label="Company / Employer"><Input className={inputCls} value={form.employer} onChange={set("employer")} data-testid={`${p}-employer`} /></Field>
      <Field label="Category" required><SelectField value={form.category} onChange={set("category")} options={categories} testId={`${p}-category`} /></Field>
      {kind === "legal" ? (
        <Field label="Urgency"><SelectField value={form.urgency} onChange={set("urgency")} options={["normal", "urgent"]} testId={`${p}-urgency`} /></Field>
      ) : <div className="hidden sm:block" />}
      <Field label="Subject" required className="sm:col-span-2"><Input className={inputCls} required value={form.subject} onChange={set("subject")} data-testid={`${p}-subject`} /></Field>
      <Field label="Describe the issue" required className="sm:col-span-2" hint="Include relevant dates and facts. Minimum 10 characters.">
        <Textarea rows={5} required minLength={10} value={form.description} onChange={set("description")} className="bg-white" data-testid={`${p}-description`} />
      </Field>
      <label className="flex items-start gap-3 text-sm text-slate-600 sm:col-span-2">
        <Checkbox checked={form.confidential} onCheckedChange={(v) => setForm((f) => ({ ...f, confidential: !!v }))} data-testid={`${p}-confidential`} />
        <span>Keep my identity confidential. The information shared will be used only to review and respond to this request.</span>
      </label>
      <div className="sm:col-span-2">{human.ui}</div>
      <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Your details are stored securely and not shared publicly.</p>
        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid={`${p}-submit`}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel}
        </button>
      </div>
    </form>
  );
};
